<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="deep-gl" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("USERS.SUB_TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('users-create')"
          class="mr-4 warning"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="users-table"
              height="500"
              name="users-table"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :filters="filters"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in columnHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <TableRowActions
                  v-if="header.value === 'options'"
                  :key="index"
                  :show-delete="false"
                >
                  <template #view>
                    <v-btn
                      icon
                      color="indigo"
                      :to="{ name: 'view-user', params: { id: item.item.id } }"
                      @click="rememberTableRow(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-eye
                      </v-icon>
                    </v-btn>
                  </template>
                </TableRowActions>
                <template v-else-if="header.value === 'active'">
                  <v-chip
                    x-small
                    :color="item.value ? 'success' : 'error'"
                    dark
                  >
                    {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                  </v-chip>
                </template>
                <template v-else-if="header.value === 'is_admin'">
                  <v-chip
                    x-small
                    :color="item.value ? 'success' : 'error'"
                    dark
                  >
                    {{ item.value ? $t("GENERAL.LABELS.YES") : $t("GENERAL.LABELS.NO") }}
                  </v-chip>
                </template>
                <template v-else-if="header.value === 'teams'">
                  {{ formatTeams(item.value) }}
                </template>
                <template v-else>
                  {{ item.value ?? "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TableRowActions from "@/views/partials/v-table/components/TableRowActions.vue";
import { GET_USERS } from "@/store/users.module";
import { GET_TEAMS_OPTIONS } from "@/store/teams.module";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";

export default {
  name: "ShowUsers",
  components: {
    Table,
    TableRowActions,
  },
  data() {
    return {
      tableName: "users-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_USERS,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "20%",
        },
        {
          text: this.$t("GENERAL.LABELS.EMAIL"),
          value: "email",
          align: "center",
          width: "22%",
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "active",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ],
      hiddenHeaders: [
        {
          text: this.$t("MENU.ASIDE.TEAMS"),
          value: "teams",
          align: "center",
          width: "18%",
          sortable: false,
        },
        {
          text: this.$t("USERS.SYSTEM_ADMIN"),
          value: "is_admin",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("GENERAL.LABELS.CREATED_DATE"),
          value: "created_at",
          align: "center",
          width: "12%",
        },
      ],
      filters: [
        {
          key: "active",
          label: this.$t("GENERAL.LABELS.STATUS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [
            { id: 1, label: this.$t("GENERAL.LABELS.ACTIVE") },
            { id: 0, label: this.$t("GENERAL.LABELS.INACTIVE") },
          ],
        },
        {
          key: "role_id",
          label: this.$t("MENU.ASIDE.TEAMS"),
          type: "select",
          "item-value": "id",
          "item-text": "name",
          items: [],
        },
        {
          key: "is_admin",
          label: this.$t("USERS.SYSTEM_ADMIN"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [
            { id: 1, label: this.$t("GENERAL.LABELS.YES") },
            { id: 0, label: this.$t("GENERAL.LABELS.NO") },
          ],
        },
      ],
    };
  },
  computed: {
    columnHeaders() {
      return [...this.headers, ...this.hiddenHeaders];
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    const selectedHiddenHeadersKey = `table-state.${this.tableName}.selectedHiddenHeaders`;
    const selectedHiddenHeadersInitializedKey = `table-state.${this.tableName}.selectedHiddenHeadersInitialized`;

    if (!userStateService.getState(selectedHiddenHeadersInitializedKey, false)) {
      userStateService.setState(selectedHiddenHeadersKey, [...this.hiddenHeaders]);
      userStateService.setState(selectedHiddenHeadersInitializedKey, true);
    }

    this.loadFilterOptions().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    formatTeams(teams) {
      if (!Array.isArray(teams) || !teams.length) {
        return "-";
      }

      return teams.map((team) => team.name).filter(Boolean).join(", ") || "-";
    },
    loadFilterOptions() {
      return this.$store
        .dispatch(GET_TEAMS_OPTIONS)
        .then((teams) => {
          this.filters.find((filter) => filter.key === "role_id").items = teams || [];
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        });
    },
    handleCreateClick() {
      this.$router.push({ name: "new-user" });
    },
    rememberTableRow(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );
    },
  },
};
</script>
